"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";

import { Advertisement } from "../../../../Redux/E-commerce Ads Model/types";
import { fetchStatusAds, setSelectedStatusAd } from "../../../../Redux/E-commerce Ads Model/slice";

const AdsStatusCards = () => {
  const dispatch = useAppDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerMobileRef = useRef<HTMLDivElement>(null);
  const { statusAds, statusAdsPagination, loading, error } = useAppSelector(
    (state) => state.eCommerceAds
  );

  // Drag scroll state
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasMoved, setHasMoved] = useState(false);

  const handleStatusAdClick = useCallback(
    (statusAd: Advertisement, e?: React.MouseEvent) => {
      // Only trigger click if user didn't drag
      if (!hasMoved) {
        dispatch(setSelectedStatusAd(statusAd));
      }
    },
    [dispatch, hasMoved]
  );

  // Drag scroll handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const container = scrollContainerRef.current || scrollContainerMobileRef.current;
    if (!container) return;
    
    setIsDragging(true);
    const rect = container.getBoundingClientRect();
    setStartX(e.pageX - rect.left);
    setScrollLeft(container.scrollLeft);
    setHasMoved(false);
    container.style.cursor = 'grabbing';
    container.style.userSelect = 'none';
  }, []);

  const handleMouseUp = useCallback(() => {
    const container = scrollContainerRef.current || scrollContainerMobileRef.current;
    if (container) {
      container.style.cursor = 'grab';
      container.style.userSelect = '';
    }
    setIsDragging(false);
    // Reset hasMoved after a short delay to allow click event
    setTimeout(() => setHasMoved(false), 100);
  }, []);

  const handleMouseLeave = useCallback(() => {
    const container = scrollContainerRef.current || scrollContainerMobileRef.current;
    if (container) {
      container.style.cursor = 'grab';
      container.style.userSelect = '';
    }
    setIsDragging(false);
  }, []);

  // Mouse wheel scroll handler
  const handleWheel = useCallback((e: React.WheelEvent) => {
    const container = scrollContainerRef.current || scrollContainerMobileRef.current;
    if (!container) return;
    
    // Prevent vertical scrolling, allow horizontal scrolling
    if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) {
      e.preventDefault();
      container.scrollLeft += e.deltaY;
    }
  }, []);

  useEffect(() => {
    dispatch(fetchStatusAds({ page: 1, limit: 10 }) as any);
  }, [dispatch]);

  // Add global mouse event listeners for drag scrolling
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      
      const container = scrollContainerRef.current || scrollContainerMobileRef.current;
      if (!container) return;
      
      const x = e.pageX - container.getBoundingClientRect().left;
      const walk = (x - startX) * 2;
      container.scrollLeft = scrollLeft - walk;
      
      if (Math.abs(walk) > 5) {
        setHasMoved(true);
      }
    };

    const handleGlobalMouseUp = () => {
      const container = scrollContainerRef.current || scrollContainerMobileRef.current;
      if (container) {
        container.style.cursor = 'grab';
        container.style.userSelect = '';
      }
      setIsDragging(false);
      setTimeout(() => setHasMoved(false), 100);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, startX, scrollLeft]);

  const getStatusColor = (index: number): string => {
    const colors = [
      "bg-success",
      "bg-primary",
      "bg-dark",
      "bg-danger",
      "bg-warning",
      "bg-light",
    ];
    return colors[index % colors.length];
  };

  if (loading.statusAds) {
    return (
      <div className="d-flex justify-content-center align-items-center min-height-200">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    console.error("=== Portfolio Error ===", error);
    return (
      <div className="alert alert-danger" role="alert">
        <h5>Error loading status ads</h5>
        <p>{error}</p>
        <button
          className="btn btn-outline-danger btn-sm"
          onClick={() =>
            dispatch(fetchStatusAds({ page: 1, limit: 10 }) as any)
          }
        >
          Retry
        </button>
      </div>
    );
  }

  if (statusAds.length === 0) {
    return (
      <div className="text-center py-4">
        <h5>No status ads available</h5>
        <p className="text-muted">
          {statusAds.length > 0
            ? `Found ${statusAds.length} status ads.`
            : "No status ads found. Please check your API connection."}
        </p>
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={() =>
            dispatch(fetchStatusAds({ page: 1, limit: 10 }) as any)
          }
        >
          Refresh
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="display-max">
        <div className="position-relative">
          <div
            ref={scrollContainerRef}
            className="d-flex justify-content-start align-items-center gap-1 mt-3 mb-3 overflow-x-auto"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none',
            }}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onWheel={handleWheel}
          >
            {statusAds.map((statusAd: Advertisement, index: number) => (
              <div
                key={statusAd._id}
                className={`cursor-pointer flex-shrink-0 ${
                  index === 0 ? "active" : ""
                }`}
                id={`v-pills-ads-status-${index + 1}-tab`}
                data-bs-toggle="pill"
                data-bs-target="#v-pills-ads-status-1"
                role="tab"
                aria-controls="v-pills-ads-status-1"
                aria-selected={index === 0 ? "true" : "false"}
                onClick={(e) => handleStatusAdClick(statusAd, e)}
              >
                <div className="card-image-2 mx-auto detail-card-header min-height-200 border-radius-2xl" style={{position: "relative" ,width:"100%", height:"100%" , overflow:"hidden" , borderRadius:"1rem"}}>
                  <img
                    className="image-size-updates min-height-200"
                    src={
                      statusAd.advertisementDurationandPricing
                        .advertisementMediaUrls ||
                      "../../assets/img/profile/profile-3.jpg"
                    }
                    alt={statusAd.advertisementTitle.slice(0, 20)}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "../../assets/img/profile/profile-3.jpg";
                    }}
                    style={{ width: "7.2rem", height: "12rem", objectFit: "cover" }}
                  />
                 <div
  style={{
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 60%, rgba(0,0,0,0.8) 100%)"
  }}
>
</div>

                  <div className="offer-text-detail container-fluid">
                    <div className="border-radius-2xl p-0">
                      <div>
                        <div className="d-flex">
                          <div
                            style={{ position:"absolute" , bottom:"0" , left:"0" }}
                            className={`text-center ${getStatusColor(
                              index
                            )} categories-bg-mini mt-1`}
                          >
                            <img
                              alt={statusAd.advertisementTitle.slice(0, 20)}
                              src={
                                statusAd.advertisementDurationandPricing
                                  .advertisementMediaUrls ||
                                "../assets/img/profile/profile-3.jpg"
                              }
                              className="avatar categories-img-product-mini img-product p-1"
                              style={{   }} 
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src =
                                  "../assets/img/profile/profile-3.jpg";
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="display-mini display-mob">
        <div
          ref={scrollContainerMobileRef}
          className="d-flex justify-content-start align-items-center gap-1 mt-3 mb-3 overflow-x-auto"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
          }}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onWheel={handleWheel}
        >
          {statusAds.map((statusAd: Advertisement, index: number) => (
            <div key={statusAd._id} className="flex-shrink-0">
              <div onClick={(e) => handleStatusAdClick(statusAd, e)}>
                <div className="card-image-2 mx-auto detail-card-header min-height-200">
                  <img
                    className="image-size-updates min-height-200"
                    src={
                      statusAd.advertisementDurationandPricing
                        .advertisementMediaUrls ||
                      "../../assets/img/profile/profile-3.jpg"
                    }
                    alt={statusAd.advertisementTitle.slice(0, 20)}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "../../assets/img/profile/profile-3.jpg";
                    }}
                  />
                  <div className="offer-text-detail container-fluid">
                    <div className="border-radius-2xl p-0">
                      <div>
                        <div className="d-flex">
                          <div
                            className={`text-center ${getStatusColor(
                              index
                            )} categories-bg-mini mt-1`}
                          >
                            <img
                              alt={statusAd.advertisementTitle.slice(0, 20)}
                              src={
                                statusAd.advertisementDurationandPricing
                                  .advertisementMediaUrls ||
                                "../assets/img/profile/profile-3.jpg"
                              }
                              className="avatar categories-img-product-mini img-product p-1"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src =
                                  "../assets/img/profile/profile-3.jpg";
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .overflow-x-auto::-webkit-scrollbar {
          display: none;
        }
        .flex-shrink-0 {
          flex-shrink: 0;
        }
      `}</style>
    </>
  );
};

export default AdsStatusCards;
